import { Outlet, Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { RunnerModal } from './RunnerModal'
import { CommandPalette } from './CommandPalette'
import { NotificationCenter } from './NotificationCenter'
import { useAuth } from './AuthGate'
import { useState, useEffect } from 'react'
import clsx from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getStoredApiKey, healthApi, setStoredApiKey } from '../utils/api'

export function Layout() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false)
  const [apiKeyValue, setApiKeyValue] = useState('')
  const [hasApiKey, setHasApiKey] = useState<boolean>(() => !!getStoredApiKey())

  const {
    data: health,
    error: healthError,
    isLoading: healthLoading,
  } = useQuery({
    queryKey: ['health'],
    queryFn: healthApi.get,
    refetchInterval: 30000,
    retry: false,
  })

  const healthMessage = (() => {
    if (healthLoading) return 'Checking…'
    if (health?.status === 'ok') return 'Live'
    if (health?.status === 'degraded') return 'Degraded'
    if (!healthError) return 'Offline'
    const msg = healthError instanceof Error ? healthError.message : 'Offline'
    if (msg.toLowerCase().includes('unauthorized')) return hasApiKey ? 'Auth Failed' : 'Auth Required'
    return 'Offline'
  })()

  const healthDotClass = (() => {
    if (healthLoading) return 'bg-signal-amber animate-pulse'
    if (health?.status === 'ok') return 'bg-signal-green animate-pulse'
    if (health?.status === 'degraded') return 'bg-signal-amber animate-pulse'
    const msg = healthError instanceof Error ? healthError.message : ''
    if (msg.toLowerCase().includes('unauthorized')) return hasApiKey ? 'bg-signal-red' : 'bg-signal-amber'
    return 'bg-signal-red'
  })()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // Load stored API key when modal opens
  useEffect(() => {
    if (!apiKeyModalOpen) return
    setApiKeyValue(getStoredApiKey() || '')
  }, [apiKeyModalOpen])

  const navItems = [
    { path: '/', label: 'Dashboard', icon: '◉', ariaLabel: 'Go to Dashboard' },
    { path: '/questions', label: 'Questions', icon: '◎', ariaLabel: 'View Questions' },
    { path: '/runs', label: 'Runs', icon: '▶', ariaLabel: 'View Prediction Runs' },
    { path: '/positions', label: 'Positions', icon: '◈', ariaLabel: 'View Positions' },
    { path: '/wallets', label: 'Wallets', icon: '◆', ariaLabel: 'View Wallets' },
    { path: '/research', label: 'Research', icon: '⬡', ariaLabel: 'View Research' },
    { path: '/skills', label: 'Skills', icon: '⚙', ariaLabel: 'Manage Skills' },
    { path: '/memory', label: 'Memory', icon: '◇', ariaLabel: 'View Memory' },
    { path: '/calibration', label: 'Calibration', icon: '📊', ariaLabel: 'View Calibration Metrics' },
    { path: '/forecast', label: 'Forecast', icon: '🔮', ariaLabel: 'Make Forecasts' },
  ]

  const isNavItemActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    if (path === '/questions') {
      return location.pathname.startsWith('/questions') || location.pathname.startsWith('/question/')
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card rounded-none border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <div className="relative">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-signal-green/20 to-signal-blue/20 flex items-center justify-center border border-signal-green/30">
                <span className="text-signal-green text-lg sm:text-xl">◉</span>
              </div>
              <div className="absolute inset-0 rounded-full bg-signal-green/20 animate-ping opacity-30"></div>
            </div>
            <div>
              <h1 className="font-display text-base sm:text-lg font-semibold text-star tracking-tight">
                Observatory
              </h1>
              <p className="text-[10px] sm:text-xs text-star-muted font-mono hidden xs:block">Prediction Explorer</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            {navItems.map((item) => {
              const isActive = isNavItemActive(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-label={item.ariaLabel}
                  aria-current={isActive ? 'page' : undefined}
                  className={clsx(
                    'px-3 xl:px-4 py-2 rounded-lg font-mono text-sm transition-all duration-200 flex items-center gap-2',
                    isActive
                      ? 'bg-signal-green/10 text-signal-green border border-signal-green/30'
                      : 'text-star-muted hover:text-star hover:bg-void-100'
                  )}
                >
                  <span className="opacity-70" aria-hidden="true">{item.icon}</span>
                  <span className="hidden xl:inline">{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* Right side: Status + Mobile menu button */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Status indicator */}
            <div
              className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-mono text-star-muted"
              role="status"
              aria-label={`Server status: ${healthMessage}`}
            >
              <span className={clsx('w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full', healthDotClass)} aria-hidden="true" />
              <span className="hidden sm:inline">{healthMessage}</span>
            </div>

            {/* Search shortcut hint */}
            <button
              onClick={() => {
                // Trigger cmd+k by dispatching keyboard event
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
              }}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-void-100 text-star-muted border-star-dim/30 hover:bg-void-200 hover:text-star text-xs font-mono transition-colors"
              aria-label="Open command palette"
            >
              <span aria-hidden="true">⌘</span>K
            </button>

            {/* API key button */}
            <button
              onClick={() => setApiKeyModalOpen(true)}
              className={clsx(
                'hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-mono transition-colors',
                hasApiKey
                  ? 'bg-signal-green/10 text-signal-green border-signal-green/30 hover:bg-signal-green/15'
                  : 'bg-void-100 text-star-muted border-star-dim/30 hover:bg-void-200 hover:text-star'
              )}
              aria-label="Set API key"
            >
              <span className="opacity-80" aria-hidden="true">🔑</span>
              <span>{hasApiKey ? 'Key Set' : 'Set Key'}</span>
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-star-muted hover:text-star hover:bg-void-100 transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

      </header>

      {/* Mobile Navigation Drawer - OUTSIDE header to avoid stacking context issues */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-void/80 backdrop-blur-sm z-[200] lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer - full width on mobile, 384px on sm+ */}
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 right-0 w-full xs:w-[85vw] sm:w-96 max-w-md bg-nebula border-l border-star-dim/20 z-[201] lg:hidden flex flex-col h-screen"
            >
              {/* Drawer header */}
              <div className="shrink-0 p-4 sm:p-5 border-b border-star-dim/20 flex items-center justify-between">
                <span className="font-mono text-base sm:text-sm text-star-muted">Navigation</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-star-muted hover:text-star hover:bg-void-100 transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto py-4" role="navigation" aria-label="Mobile navigation">
                {navItems.map((item) => {
                  const isActive = isNavItemActive(item.path)
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-label={item.ariaLabel}
                      aria-current={isActive ? 'page' : undefined}
                      className={clsx(
                        'flex items-center gap-4 px-6 py-5 min-h-[64px] font-mono text-lg transition-all duration-200',
                        isActive
                          ? 'bg-signal-green/10 text-signal-green border-r-4 border-signal-green'
                          : 'text-star-muted hover:text-star hover:bg-void-100'
                      )}
                    >
                      <span className="text-2xl opacity-70" aria-hidden="true">{item.icon}</span>
                      {item.label}
                    </Link>
                  )
                })}
              </nav>

              {/* Drawer footer */}
              <div className="shrink-0 p-5 sm:p-4 border-t border-star-dim/20">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm sm:text-xs font-mono text-star-dim">
                    <span className={clsx('w-2 h-2 rounded-full', healthDotClass)} />
                    <span>{healthMessage}</span>
                  </div>
                  <button
                    onClick={() => {
                      setApiKeyModalOpen(true)
                      setMobileMenuOpen(false)
                    }}
                    className={clsx(
                      'px-3 py-2 rounded-lg border text-sm sm:text-xs font-mono transition-colors',
                      hasApiKey
                        ? 'bg-signal-green/10 text-signal-green border-signal-green/30 hover:bg-signal-green/15'
                        : 'bg-void-100 text-star-muted border-star-dim/30 hover:bg-void-200 hover:text-star'
                    )}
                    aria-label="Set API key"
                  >
                    🔑 {hasApiKey ? 'Key Set' : 'Set Key'}
                  </button>
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
      
      {/* Main content */}
      <main className="flex-1">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <Outlet />
        </motion.div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-star-dim/10 py-4">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between text-xs text-star-dim font-mono">
          <span>Prediction Engine v2</span>
          <span>◎ Observatory Interface</span>
        </div>
      </footer>
      
      {/* Runner Modal */}
      <RunnerModal />

      {/* Command Palette (⌘K) */}
      <CommandPalette />

      {/* Notification Center */}
      <NotificationCenter />

      {/* API Key Modal */}
      <AnimatePresence>
        {apiKeyModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-void/80 backdrop-blur-sm z-[300]"
              onClick={() => setApiKeyModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[301] flex items-center justify-center p-4"
              onClick={() => setApiKeyModalOpen(false)}
            >
              <div
                className="glass-card w-full max-w-md p-5 sm:p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-display font-semibold text-star text-base">API Key</h3>
                    <p className="text-xs text-star-muted mt-1">
                      Used when `API_AUTH_TOKEN` is enabled on the server.
                    </p>
                  </div>
                  <button
                    className="p-2 -mr-2 -mt-2 rounded-lg text-star-dim hover:text-star hover:bg-void-100 transition-colors"
                    onClick={() => setApiKeyModalOpen(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <label className="data-label mb-2 block">X-API-Key</label>
                <input
                  type="password"
                  className="input w-full min-h-[44px]"
                  value={apiKeyValue}
                  onChange={(e) => setApiKeyValue(e.target.value)}
                  placeholder="Paste API key…"
                  autoFocus
                />

                <div className="flex items-center justify-between gap-3 mt-4">
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-mono bg-signal-red/10 text-signal-red border border-signal-red/30 hover:bg-signal-red/20 transition-colors"
                    onClick={() => {
                      setApiKeyModalOpen(false)
                      logout()
                    }}
                  >
                    Logout
                  </button>
                  <div className="flex items-center gap-2">
                    <button className="btn-secondary" onClick={() => setApiKeyModalOpen(false)}>
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        const trimmed = apiKeyValue.trim()
                        setStoredApiKey(trimmed || null)
                        setHasApiKey(!!trimmed)
                        queryClient.invalidateQueries({ queryKey: ['health'] })
                        setApiKeyModalOpen(false)
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
